import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { agent } from "apiClient/api"
import { useQuotation } from "context/quotationContext"
import { useSnackbar } from "context/snackbarContext"
import type { QuotationVersionItemModel } from "models/axiosModel"
import type { MarkQuotationStatusConfirmVariant } from "component/quotation/MarkQuotationStatusConfirmPopup"
import { buildQuotationStatusChangePayload } from "component/quotation/quotationStatusChangeUtil"
import type { QuotationStatusIds } from "component/quotation/quotationStatusVariant"
import { buildSkippedHotelsRouteState, readSkippedHotels } from "component/quotation/duplicateQuotationSkippedHotelsUtil"
import { quotationIdsEqual, resolveQuotationPreviewCurrentVersion } from "pages/quotation/quotationPreviewUtil"
import { buildQuotationPreviewPath } from "util/quotationPreviewPathUtil"

const DEFAULT_STATUS_IDS: QuotationStatusIds = {
	draftStatusId: 1,
	readyToSendStatusId: 2,
	sentStatusId: 3,
	followUpStatusId: 4,
	confirmedStatusId: 5,
	lostStatusId: 6,
}

const statusIdByName = (list: any[], name: string, fallback?: number): number => {
	const item = list.find((status: any) => String(status?.quotation_status_name || "").toLowerCase().trim() === name)
	return Number(item?.quotation_status_id) || Number(fallback) || 0
}

const filenameFromHeader = (contentDisposition: string | undefined, fallback: string): string => {
	const raw = contentDisposition?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1]?.trim()
	if (!raw) return fallback
	try {
		return decodeURIComponent(raw.replace(/"/g, ""))
	} catch {
		return raw.replace(/"/g, "")
	}
}

const useQuotationPreviewV2 = (quotationId: string) => {

	const navigate = useNavigate()
	const { showError, showSuccess } = useSnackbar()
	const {
		quotationData,
		setQuotationData,
		setQuotationStatusOptions,
		setCurrentQuotationStatusObj,
		currentQuotationStatusObj,
	} = useQuotation()

	const [isLoading, setIsLoading] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [statusIds, setStatusIds] = useState<QuotationStatusIds>(DEFAULT_STATUS_IDS)

	const applyDetailsResponse = (response: any) => {
		setQuotationData(response)
		const detail = response?.data?.quotation
		if (detail) {
			setCurrentQuotationStatusObj({
				quotation_status_name: detail.quotation_status_name,
				quotation_status_id: detail.quotation_status_id,
			})
		}
	}

	useEffect(() => {
		let mounted = true
		const fetchDetails = async () => {
			if (!quotationId) return
			setIsLoading(true)
			try {
				const [response, genericResponse] = await Promise.all([
					agent.QuotationDetailsAPI.get(quotationId, true),
					agent.GenericGlobalAPI.get(true).catch(() => null),
				])
				if (!mounted) return
				if (response?.status) applyDetailsResponse(response)
				const statusList = genericResponse?.status && Array.isArray(genericResponse?.data?.[0]?.quotation_status)
					? genericResponse.data[0].quotation_status
					: []
				setQuotationStatusOptions(statusList)
				setStatusIds({
					draftStatusId: statusIdByName(statusList, "draft", DEFAULT_STATUS_IDS.draftStatusId),
					readyToSendStatusId: statusIdByName(statusList, "ready to send", DEFAULT_STATUS_IDS.readyToSendStatusId),
					sentStatusId: statusIdByName(statusList, "sent", DEFAULT_STATUS_IDS.sentStatusId),
					followUpStatusId: statusIdByName(statusList, "follow up", DEFAULT_STATUS_IDS.followUpStatusId),
					confirmedStatusId: statusIdByName(statusList, "confirmed", DEFAULT_STATUS_IDS.confirmedStatusId),
					lostStatusId: statusIdByName(statusList, "lost", DEFAULT_STATUS_IDS.lostStatusId),
				})
			} finally {
				if (mounted) setIsLoading(false)
			}
		}
		fetchDetails()
		return () => {
			mounted = false
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [quotationId])

	const quotation: any = quotationData?.data?.quotation
	const isLoaded = Boolean(quotation) && quotationIdsEqual(quotation?.quotation_id, quotationId)
	const currentStatusId = Number(currentQuotationStatusObj?.quotation_status_id ?? quotation?.quotation_status_id ?? 0)

	const versions: QuotationVersionItemModel[] = useMemo(
		() => {
			const list = quotationData?.data?.quotation_versions
			return Array.isArray(list) ? list : []
		},
		[quotationData?.data?.quotation_versions],
	)
	const currentVersion = useMemo(
		() => resolveQuotationPreviewCurrentVersion(versions, quotationId, quotation),
		[versions, quotationId, quotation],
	)
	const latestVersionNo = versions.reduce((max, version) => Math.max(max, Number(version?.version_no || 0)), 0)
	const currentVersionNo = Number(currentVersion?.version_no || quotation?.version_no || 1)
	const flagViewingLatest = !latestVersionNo || currentVersionNo >= latestVersionNo

	const flagDraft = currentStatusId === statusIds.draftStatusId
	const flagCanCreateVersion = [statusIds.readyToSendStatusId, statusIds.sentStatusId, statusIds.followUpStatusId].includes(currentStatusId)
	const flagCanMarkConfirmedLost = [statusIds.sentStatusId, statusIds.followUpStatusId].includes(currentStatusId)

	const goToVersion = (versionQuotationId: string) => {
		if (!versionQuotationId || quotationIdsEqual(versionQuotationId, quotationId)) return
		navigate(buildQuotationPreviewPath(versionQuotationId))
	}

	const handleEdit = () => {
		if (!quotationId) return
		navigate(`/quotation/new?quotation_id=${encodeURIComponent(quotationId)}`)
	}

	const handleDownload = async () => {
		if (!quotationId) return
		const fallbackName = `Quotation_${quotation?.sta_code || quotationId}.docx`
		try {
			const response = await agent.QuotationDocxAPI.get(quotationId, true)
			if (!response || response.status === false || !(response.data instanceof Blob)) {
				showError("Failed to download quotation")
				return
			}
			const blobUrl = URL.createObjectURL(response.data)
			const anchor = document.createElement("a")
			anchor.href = blobUrl
			anchor.download = filenameFromHeader(response.headers?.["content-disposition"] as string | undefined, fallbackName)
			document.body.appendChild(anchor)
			anchor.click()
			anchor.remove()
			URL.revokeObjectURL(blobUrl)
		} catch {
			showError("Failed to download quotation")
		}
	}

	const duplicateQuotation = async (payload: any): Promise<boolean> => {
		if (!quotationId) return false
		setIsSubmitting(true)
		try {
			const response = await agent.QuotationDuplicateAPI.post(quotationId, payload, true)
			const newQuotationId = response?.status ? response?.data?.quotation_id : ""
			if (!newQuotationId) return false
			navigate(buildQuotationPreviewPath(newQuotationId), {
				state: buildSkippedHotelsRouteState(readSkippedHotels(response)),
			})
			return true
		} finally {
			setIsSubmitting(false)
		}
	}

	const changeStatus = async (variant: MarkQuotationStatusConfirmVariant, remark?: string): Promise<boolean> => {
		const nextStatusId = variant === "confirmed" ? statusIds.confirmedStatusId : statusIds.lostStatusId
		if (!quotationId || !nextStatusId) return false
		setIsSubmitting(true)
		try {
			const response = await agent.QuotationChangeStatusAPI.put(
				buildQuotationStatusChangePayload(quotationId, nextStatusId, variant === "lost" ? remark ?? "" : undefined),
				true,
			)
			if (!response?.status) {
				showError("Failed to update quotation status")
				return false
			}
			const refreshed = await agent.QuotationDetailsAPI.get(quotationId, true)
			if (refreshed?.status) applyDetailsResponse(refreshed)
			showSuccess(variant === "confirmed" ? "Quotation marked as confirmed" : "Quotation marked as lost")
			return true
		} catch {
			showError("Failed to update quotation status")
			return false
		} finally {
			setIsSubmitting(false)
		}
	}

	return {
		isLoading,
		isLoaded,
		isSubmitting,
		quotationData,
		quotation,
		versions,
		currentVersion,
		currentVersionNo,
		latestVersionNo,
		flagViewingLatest,
		flagDraft,
		flagCanCreateVersion,
		flagCanMarkConfirmedLost,
		goToVersion,
		handleEdit,
		handleDownload,
		duplicateQuotation,
		changeStatus,
	}
}

export default useQuotationPreviewV2
